import { Box, CircularProgress } from '@material-ui/core'
import { useFetchPurchaseArticles } from 'lib/hooks/useFetchPurchaseArticles'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import { CardLanguage, gameMetaData, PurchaseArticle, PurchaseEvent } from 'shared'

interface CsvPurchaseArticleItem {
  purchaseDate: string
  lotNumber: string
  currency: string
  sellerName: string
  vatNumber: string
  isVat: boolean
  quantity: number
  price: number
  itemName: string
  itemLocalizedName: string
  itemCollectorNumber: string
  itemRarity: string
  itemSet: string
  itemExpansion: string
  categoryName: string
  game: string
  language: string
  condition: string
  finishType: string
  buyPrice: number
  isFirstEd: boolean
  isAltered: boolean
  isSigned: boolean
  gradingCompany: string
  grade: string
  countryEdition: string
}

export interface ExportPurchaseArticlesProps {
  purchaseEvent: PurchaseEvent
}

export const ExportPurchaseArticles = ({
  purchaseEvent,
}: ExportPurchaseArticlesProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { fetchArticles } = useFetchPurchaseArticles()
  const [csvData, setCsvData] = useState<CsvPurchaseArticleItem[]>([])
  const [loading, setLoading] = useState(false)

  const handleExport = async () => {
    setLoading(true)
    try {
      const articles: PurchaseArticle[] = await fetchArticles(purchaseEvent.id)

      const data: CsvPurchaseArticleItem[] = articles.map((a) => ({
        purchaseDate: purchaseEvent.purchaseDate.toISOString(),
        lotNumber: purchaseEvent.lotNumber,
        currency: purchaseEvent.currency,
        sellerName: purchaseEvent.seller?.name ?? '',
        vatNumber: purchaseEvent.seller?.vatNumber ?? '',
        isVat: a.isVat,
        quantity: a.quantity,
        price: a.price,
        itemName: a.card.name,
        itemLocalizedName: getArticleLocalizedName(a) || '',
        itemCollectorNumber: a.card.cn || '',
        itemRarity: a.card.rarityStr || '',
        itemSet: a.card.set || '',
        itemExpansion: a.card.expansion || '',
        categoryName: a.card.categoryName || '',
        game: gameMetaData.find((g) => g.idGame === a.card.idGame)?.name || '',
        language: a.languageName,
        condition: a.condition,
        finishType: a.finishType,
        buyPrice: a.price,
        isFirstEd: a.isFirstEd,
        isAltered: a.isAltered,
        isSigned: a.isSigned,
        gradingCompany: a.gradingCompany || '',
        grade: a.grade || '',
        countryEdition: a.countryEdition,
      }))

      setCsvData(data)

      // Trigger automatic download once data is ready
      setTimeout(() => {
        document.getElementById(`csv-download-${purchaseEvent.id}`)?.click()
      }, 100)

      enqueueSnackbar(`${t('taxation.success.articlesExported')}`, { variant: 'success' })
    } catch (e) {
      console.error(e)
      enqueueSnackbar(`${t('taxation.errors.errorExportingCsv')}`, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const getArticleLocalizedName = (article: PurchaseArticle) => {
    const idLanguage = article.idLanguage
    if (idLanguage === CardLanguage.English) {
      return article.card.name
    } else if (idLanguage === CardLanguage.German) {
      return article.card.locNames.DE
    } else if (idLanguage === CardLanguage.French) {
      return article.card.locNames.FR
    } else if (idLanguage === CardLanguage.Spanish) {
      return article.card.locNames.ES
    } else if (idLanguage === CardLanguage.Italian) {
      return article.card?.locNames.IT
    }
  }

  const fileName = `${purchaseEvent.lotNumber}-${
    purchaseEvent.seller?.name ?? 'unknown'
  }-${moment().format('DD-MM-YYYY_HH-mm')}.csv`

  return (
    <Box textAlign="center">
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <i
          className="fa-light fa-arrow-down-to-bracket table-action-icon"
          onClick={handleExport}
          style={{ cursor: 'pointer' }}
        ></i>
      )}

      <CSVLink
        id={`csv-download-${purchaseEvent.id}`}
        data={csvData}
        filename={fileName}
        style={{ display: 'none' }}
        enclosingCharacter={`"`}
      />
    </Box>
  )
}
